import React from 'react';
import { CalendarRange } from 'lucide-react';
import {
  SEASONALITY_OPTIONS, INTRA_QUARTER_OPTIONS, DEFAULT_SEASONALITY,
  INTRA_QUARTER_FACTORS, illustrativeWeights, customQuartersTotal
} from './forecastSeasonality';

// Item E — reusable intra-year shape picker shared by every AI forecast entry
// point (per-line AI Auto Forecast, "prompt to modify", and the Ask AI box).
// Optional, defaulted to "Auto". Sparklines let the user see each shape; the
// backend computes the real total-preserving weights from data.

const Spark = ({ weights, active, width = 46, height = 16 }) => {
  const max = Math.max(...weights, 1e-6);
  const n = weights.length || 1;
  const bw = width / n;
  return (
    <svg className="seasonality-spark" width={width} height={height}
      viewBox={`0 0 ${width} ${height}`} aria-hidden="true" focusable="false">
      {weights.map((val, i) => {
        const barHeight = Math.max(1, (val / max) * (height - 2));
        return (
          <rect
            key={i} x={i * bw + 0.5} y={height - barHeight}
            width={Math.max(1, bw - 1.2)} height={barHeight} rx="0.6"
            className={active ? 'seasonality-spark-bar active' : 'seasonality-spark-bar'}
          />
        );
      })}
    </svg>
  );
};

const SeasonalityControl = ({
  value = DEFAULT_SEASONALITY,
  onChange,
  options,                       // optional subset of profile ids to show
  showIntraQuarter = true,
  disabled = false,
  compact = false,
  label = 'Intra-year shape',
  note                            // optional caption under the control
}) => {
  const current = { ...DEFAULT_SEASONALITY, ...(value || {}) };
  const profiles = options && options.length
    ? SEASONALITY_OPTIONS.filter((o) => options.includes(o.id))
    : SEASONALITY_OPTIONS;
  const activeOption = SEASONALITY_OPTIONS.find((o) => o.id === current.profile) || SEASONALITY_OPTIONS[0];
  const applied = illustrativeWeights(current);
  const customTotal = customQuartersTotal(current.custom);

  const set = (patch) => { if (!disabled && onChange) onChange({ ...current, ...patch }); };
  const setQuarter = (index, raw) => {
    const next = [...(current.custom?.quarters || [25, 25, 25, 25])];
    next[index] = Math.max(0, Math.min(100, Number(raw) || 0));
    set({ custom: { ...(current.custom || {}), quarters: next } });
  };

  return (
    <div className={`seasonality-control${compact ? ' compact' : ''}`}>
      <div className="seasonality-head">
        <span className="seasonality-eyebrow">
          <CalendarRange size={12} aria-hidden="true" /> {label}
        </span>
        <span className="seasonality-applied" title="Net intra-year shape that will be applied (preview)">
          <Spark weights={applied} active width={64} height={18} />
        </span>
      </div>

      <div className="seasonality-row" role="radiogroup" aria-label="Seasonality profile">
        {profiles.map((option) => {
          const isActive = option.id === current.profile;
          return (
            <button
              key={option.id} type="button" role="radio" aria-checked={isActive}
              className={`seasonality-pill${isActive ? ' selected' : ''}`}
              onClick={() => set({ profile: option.id })}
              disabled={disabled} title={option.hint}
            >
              <Spark weights={illustrativeWeights({ profile: option.id, custom: current.custom })} active={isActive} />
              <span className="seasonality-pill-label">{option.label}</span>
            </button>
          );
        })}
      </div>

      {current.profile === 'custom' && (
        <div className="seasonality-custom">
          {[0, 1, 2, 3].map((q) => (
            <label key={q} className="seasonality-custom-cell">
              <span>Q{q + 1}</span>
              <input
                type="number" min={0} max={100} step={5} inputMode="numeric"
                value={current.custom?.quarters?.[q] ?? 25}
                onChange={(e) => setQuarter(q, e.target.value)}
                disabled={disabled} aria-label={`Custom allocation Q${q + 1} percent`}
              />
              <span className="seasonality-custom-pct">%</span>
            </label>
          ))}
          <span className={`seasonality-custom-total${Math.abs(customTotal - 100) > 0.5 ? ' off' : ''}`}>
            = {customTotal}% {Math.abs(customTotal - 100) > 0.5 ? '(auto-normalized)' : '✓'}
          </span>
        </div>
      )}

      {showIntraQuarter && (
        <div className="seasonality-intra" role="radiogroup" aria-label="Intra-quarter shape">
          <span className="seasonality-intra-label">Within quarter:</span>
          {INTRA_QUARTER_OPTIONS.map((option) => {
            const isActive = option.id === (current.intraQuarter || 'even');
            return (
              <button
                key={option.id} type="button" role="radio" aria-checked={isActive}
                className={`seasonality-chip${isActive ? ' selected' : ''}`}
                onClick={() => set({ intraQuarter: option.id })}
                disabled={disabled} title={option.hint}
              >
                <Spark weights={INTRA_QUARTER_FACTORS[option.id]} active={isActive} width={20} height={14} />
                {option.label}
              </button>
            );
          })}
        </div>
      )}

      <p className="seasonality-hint">{activeOption.hint}</p>
      {note && <p className="seasonality-hint subtle">{note}</p>}
    </div>
  );
};

export default SeasonalityControl;
